import React from 'react';
import { Box, IconButton, MenuItem, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import { Link as RouterLink } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import BrokerAvatar from '@/components/BrokerAvatar';

const formatTimestamp = (value) => {
	if (!value) return '';
	const date = typeof value === 'number' ? new Date(value) : new Date(value);
	if (Number.isNaN(date.getTime())) return '';
	return formatDistanceToNow(date, { addSuffix: true });
};

/**
 * One notification in the notifications menu: links to `metadata.path` when it has one,
 * with an unread dot and an archive button.
 *
 * @param {Object} props
 * @param {Object} props.notification - An event notification from listEventNotifications.
 * @param {Function} props.onClick - Called with the click event.
 * @param {Function} props.onArchive - Called with the archive button's click event.
 * @param {boolean} [props.divider] - Draws a divider below the item.
 * @returns {JSX.Element}
 */
const NotificationMenuItem = ({ notification, onClick, onArchive, divider }) => {
	const theme = useTheme();

	return (
		<MenuItem
			{...(notification.metadata?.path && {
				component: RouterLink,
				to: notification.metadata.path,
			})}
			divider={divider}
			onClick={onClick}
			sx={{
				alignItems: 'flex-start',
				whiteSpace: 'normal',
			}}
		>
			<Stack direction='row' spacing={1.5} width='100%' alignItems='flex-start'>
				<Box
					sx={{
						width: 8,
						flexShrink: 0,
						pt: 0.75,
					}}
				>
					{notification.status === 'unread' && (
						<Box
							sx={{
								width: 8,
								height: 8,
								borderRadius: '50%',
								backgroundColor: theme.palette.primary.main,
							}}
						/>
					)}
				</Box>
				<BrokerAvatar broker={{ id: notification.actorUserId }} width={32} height={32} />
				<Stack spacing={0.5} flex={1} minWidth={0} pt={0.5}>
					<Typography variant='task_body' fontWeight={600}>
						{notification.title}
					</Typography>
					<Typography variant='task_body' color='text.secondary'>
						{notification.message}
					</Typography>
					<Typography variant='task_updated'>
						{formatTimestamp(notification.createdAt)}
					</Typography>
				</Stack>
				{notification.status !== 'archived' && (
					<Tooltip title='Archive notification'>
						<span>
							<IconButton
								size='small'
								aria-label='Archive notification'
								onClick={onArchive}
								sx={{
									flexShrink: 0,
									alignSelf: 'flex-start',
									width: 28,
									height: 28,
									p: 0.5,
								}}
							>
								<ArchiveOutlinedIcon sx={{ fontSize: 18 }} />
							</IconButton>
						</span>
					</Tooltip>
				)}
			</Stack>
		</MenuItem>
	);
};
NotificationMenuItem.displayName = 'NotificationMenuItem';
export default NotificationMenuItem;
